/**
 * The read contract for the Twin 3D view settings, in shared/.
 *
 * Round 10 A5 put the tenant hop delay on two surfaces at once: the
 * cascade scene, which computes with it, and the edge-delay register,
 * which is where every undeclared edge falls back to it. The register
 * lives in `shared/admin/` and may not import from `twin/`, so the
 * QUERY KEY and the DEFAULT live here and the twin hook reads them --
 * one cache entry, one fallback figure, no second copy of 22 waiting
 * to disagree with the first.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";

/** Minutes for an impairment to cross one dependency hop. */
export const DEFAULT_HOP_DELAY_MIN = 22;

export const twin3dSettingsKey = ["twin", "view-settings"] as const;

/** One tenant-set figure with its provenance (round 10 A5). */
export interface Twin3DFigure {
  key: string;
  value: number;
  default_value: number;
  /** NULL while the row has only ever held its seeded default. */
  changed_by: string | null;
  changed_at: string | null;
}

export interface Twin3DSettings {
  fragility_weights: {
    ibs: number;
    crit: number;
    sole_supplier: number;
    impaired: number;
    sole_vendor_impaired: number;
  };
  cascade_hop_delay_minutes: number;
  figures?: Twin3DFigure[];
}

export function useTwin3DSettings() {
  return useQuery({
    queryKey: twin3dSettingsKey,
    queryFn: () => api.get<Twin3DSettings>("/v1/twin/view-settings"),
    // Rarely edited; no reason to refetch on every view switch.
    staleTime: 5 * 60_000,
  });
}

/**
 * The hop delay to quote.
 *
 * A non-positive figure would put every node at T+0 and make the clock
 * meaningless, so it falls back rather than being obeyed -- the same
 * rule the server applies, stated in both places because either can be
 * the one a reader is looking at.
 */
export function hopDelayFrom(settings: Twin3DSettings | undefined): number {
  const n = settings?.cascade_hop_delay_minutes;
  return typeof n === "number" && n > 0 ? n : DEFAULT_HOP_DELAY_MIN;
}
